import { Icons } from '@ds/Icons'
import {
	ColDef,
	Column,
	ColumnResizedEvent,
	FirstDataRenderedEvent,
	GridApi,
	GridReadyEvent,
	IServerSideDatasource,
	SelectionChangedEvent,
} from 'ag-grid-enterprise'

import { BTN } from '@/styles/components/button'

export enum SqlOperator {
	EQUALS = '=',
	NOT_EQUALS = '!=',
	GREATER_THAN = '>',
	LESS_THAN = '<',
	GREATER_THAN_OR_EQUALS = '>=',
	LESS_THAN_OR_EQUALS = '<=',
	IS_NULL = 'IS_NULL',
	IS_NOT_NULL = 'IS_NOT_NULL',
	IN = 'IN',
	NOT_IN = 'NOT_IN',
	CONTAINS = 'CONTAINS',
	NOT_CONTAINS = 'NOT_CONTAINS',
	STARTS_WITH = 'STARTS_WITH',
	ENDS_WITH = 'ENDS_WITH',
	BETWEEN = 'BETWEEN',
}

export const operatorLabels: Partial<Record<SqlOperator, string>> = {
	[SqlOperator.EQUALS]: 'equals',
	[SqlOperator.NOT_EQUALS]: 'does not equal',
	[SqlOperator.IN]: 'is any of',
	[SqlOperator.NOT_IN]: 'is not any of',
	[SqlOperator.CONTAINS]: 'contains',
	[SqlOperator.NOT_CONTAINS]: 'does not contain',
	[SqlOperator.IS_NULL]: 'is not defined',
	[SqlOperator.IS_NOT_NULL]: 'is defined',
	[SqlOperator.BETWEEN]: 'is between',
	[SqlOperator.STARTS_WITH]: 'starts with',
	[SqlOperator.ENDS_WITH]: 'ends with',
}

export const operatorHasValue = (operator: SqlOperator) => {
	return (
		operator !== SqlOperator.IS_NULL && operator !== SqlOperator.IS_NOT_NULL
	)
}

type OperatorValueType<T, O extends SqlOperator> = O extends
	| SqlOperator.IN
	| SqlOperator.NOT_IN
	? T[]
	: O extends SqlOperator.IS_NULL | SqlOperator.IS_NOT_NULL
		? null
		: O extends
					| SqlOperator.CONTAINS
					| SqlOperator.NOT_CONTAINS
					| SqlOperator.STARTS_WITH
					| SqlOperator.ENDS_WITH
			? string
			: O extends SqlOperator.BETWEEN
				? [T, T]
				: T

interface BaseFilterOperation {
	caseSensitive?: boolean
	negate?: boolean
}

export type FilterOperation<
	T = any,
	O extends SqlOperator = SqlOperator,
> = BaseFilterOperation & {
	operator: O
	value: OperatorValueType<T, O>
}

export type PaginationParams = {
	from: number
	to: number
}

export type SortParams<T> = {
	field: keyof T
	direction: 'asc' | 'desc'
}

export type FilterOpsV2<T> = {
	attributesFilters?: FilterState<T>
	paginationArgs?: PaginationParams
	sortingState?: SortParams<T>
	searchQuery?: string
}

export type FilterState<T> = {
	[K in keyof T]?: FilterOperation<T[K]>
}

export type TableRefetchParams<T> = {
	filters?: FilterState<T>
	dateFilter?: DateFilterType | null
	page?: number // 0-based index
	from?: number
	to?: number
	searchQuery?: string
	sort?: SortState<T>
}

type FilterableRefetchResult<T> = { rowData: T[]; rowCount: number }

type FilterableRefetch<T> = (
	params: TableRefetchParams<T>,
) => FilterableRefetchResult<T> | Promise<FilterableRefetchResult<T>>

interface BaseTableDataSource<T> {
	data: T[]
	isLoading: boolean
	totalCount: number
	pageSize: number
}

type ServerSideBucket = {
	value: string
	childCount: number
}

export type ServerSideBucketFetchParams<T> = {
	filters?: FilterState<T>
	searchQuery?: string
}

type ServerSideBucketFetchResult = {
	buckets: ServerSideBucket[]
	totalCount: number
}

type ServerSideBucketFetch<T> = (
	groupKeys: (keyof T)[],
	params: ServerSideBucketFetchParams<T>,
) => Promise<ServerSideBucketFetchResult>

export interface SimpleTableDataSource<T> extends BaseTableDataSource<T> {
	refetch?: () => void
}

export interface FilterableTableDataSource<T> extends BaseTableDataSource<T> {
	refetch: FilterableRefetch<T>
}

export interface ServerSideTableDataSource<T>
	extends FilterableTableDataSource<T> {
	fetchBuckets: ServerSideBucketFetch<T>
}

export type TableDataSource<T> =
	| SimpleTableDataSource<T>
	| FilterableTableDataSource<T>
	| ServerSideTableDataSource<T>

interface SelectionConfig<T> {
	type: 'single' | 'multiple'
	onSelectionChange?: (selected: T[]) => void
}

interface BulkActionBase<T> {
	id: string
	label: string
	icon?: Icons | string // string for custom images
	disabled?: boolean | ((selected: T[]) => boolean)
	tooltip?: string | ((selected: T[]) => string)
	group?: string
}

export interface BulkActionButton<T> extends BulkActionBase<T> {
	handler: (selected: T[]) => void | Promise<void>
	buttonVariant?: keyof typeof BTN
}

interface BulkActionLeaf<T> extends BulkActionBase<T> {
	handler: (selected: T[]) => void | Promise<void>
	children?: never
}

interface BulkActionBranch<T> extends BulkActionBase<T> {
	handler?: never
	children: BulkActionMenuItem<T>[]
}

export type BulkActionMenuItem<T> = BulkActionBranch<T> | BulkActionLeaf<T>

export interface BulkActionsConfig<T> {
	buttons?: BulkActionButton<T>[]
	menuItems?: BulkActionMenuItem<T>[]
}

export interface SortState<T> {
	field: keyof T
	direction: 'asc' | 'desc'
}

interface SortingConfig<T> {
	defaultSort?: SortState<T>
	onSortChange?: (sort: SortState<T> | undefined) => void
	comparator?: (a: T, b: T) => number
}

export interface FilterCategoryLeaf<T> {
	field: keyof T
	label: string
	icon?: Icons
	options?: Array<{
		label: string
		value: string | number
	}>
	dataType?: ColumnDataType
	operators?: SqlOperator[]
	children?: never
}

export interface FilterCategoryBranch<T> {
	label: string
	icon?: Icons
	children: FilterCategory<T>[]
	field?: never
	options?: never
	dataType?: never
	operators?: never
}

export type FilterCategory<T> = FilterCategoryLeaf<T> | FilterCategoryBranch<T>

export interface FilteringConfig<T> {
	filters: FilterCategory<T>[]
	defaultFilters?: FilterState<T>
	onFilterChange?: (filters: FilterState<T>) => void
}

interface GroupingConfig<T> {
	groupableFields: GroupableField<T>[]
	onGroupingChange?: (groupedFields: GroupableField<T>[]) => void
}

interface DateFilteringConfig {
	enabled: true
	defaultDateFilter?: DateFilterType
	onDateFilterChange?: (dateFilter: DateFilterType | undefined) => void
}

interface PaginationConfig {
	enabled: true
}

export interface DateFilterType {
	value?: DateFilterEnum
	startValue?: string
	endValue?: string
}

export enum DateFilterEnum {
	today = 'today',
	yesterday = 'yesterday',
	last_week = 'last_week',
	last_month = 'last_month',
	last_year = 'last_year',
	custom = 'custom',
}

export interface TableView<T> {
	id: string
	name: string
	filters?: FilterState<T>
	dateFilter?: DateFilterType
	groupBy?: GroupableField<T>[]
	sortingState?: SortParams<T>
	hiddenColumns?: (keyof T)[]
}

// TODO: Locate properly, but this is the DB representation of TableView<T>
export interface ViewType<T> {
	group_by: GroupableField<T>[]
	id: string
	team_id?: number
	name?: string | null
	hidden_fields?: (keyof T)[] | null
	attribute_filters?: FilterState<T> | null // Changed to be 1:1 JSON with Table2
	created_at?: string
	updated_at?: string
	deleted_at?: string | null

	date_filter?: null // Unused for now
	stage_filters?: string[] | null // No longer needed

	// sortingState?: SortParams<T> | null // Doesn't exist in DB yet
}

export type ViewsType<T> = Record<string, ViewType<T>>

export interface ViewsConfig<T> {
	initialViewId?: string
	systemViewId: string
	views: TableView<T>[]
	mutations: {
		createView: (payload: ViewType<T>) => Promise<ViewType<T>>
		updateView: (payload: ViewType<T>) => Promise<ViewType<T>>
		deleteView: (id: string) => Promise<void>
	}
	onViewChange?: (viewId: string) => void
}

interface SearchConfig {
	enabled: true
	debounceMs?: number
	onChange?: (query: string) => void
}

interface TableFeatures<T> {
	selection?: SelectionConfig<T>
	bulkActions?: BulkActionsConfig<T>
	sorting?: SortingConfig<T>
	filtering?: FilteringConfig<T>
	grouping?: GroupingConfig<T>
	dateFiltering?: DateFilteringConfig
	views?: ViewsConfig<T>
	search?: SearchConfig
	contextMenu?: ContextMenuConfig<T>
	editing?: EditingConfig<T>
	pagination?: PaginationConfig
}

export type CellRendererProps<T, K extends keyof T | '*'> = {
	value: K extends keyof T ? T[K] : string
	row: T
	column: Column
}

type TableCellRenderers<T> = {
	[K in keyof T | '*']?: (props: CellRendererProps<T, K>) => React.ReactNode
}

export type HeaderRendererProps<T> = {
	header?: string
	column: Column
	field: keyof T
}

type TableHeaderRenderers<T> = {
	[K in keyof T | '*']?: (props: HeaderRendererProps<T>) => React.ReactNode
}

export interface TableContextState<T> {
	config: TableProviderConfig<T>
	data: T[]
	isLoading: boolean
	isServerSideLoading: boolean
	totalCount: number
	gridApi?: GridApi
	currentPage: number
	resizingColumn: string | null
	isGrouped: boolean
	activeFilters: FilterState<T>
	selectedIds: Set<string>
	bulkActionsVisible: boolean
	activeSort: SortState<T> | undefined
	activeDateFilter: DateFilterType | undefined
	groupedColumns: string[]
	currentViewId: string | null
	currentView: TableView<T> | null
	hasViewChanges: boolean
	isSystemView: boolean
	views: TableView<T>[] | null
	hasFirstDataRendered: boolean
	searchQuery: string
	isEditingBlocked: boolean
	serverSideDatasource: IServerSideDatasource | undefined
	currentPageIds: string[]
	handleSelectAllRows: () => void
	onGridReady: (params: GridReadyEvent) => void
	onFirstDataRendered: (params: FirstDataRenderedEvent) => void
	handleColumnResized: (event: ColumnResizedEvent) => void
	handleSetSelectedRow: (id: string) => void
	handleDeselectAllRows: () => void
	getSelectedItems: () => T[]
	setGroupedColumns: (columns: string[]) => void
	handleGroupBy: (field: GroupableField<T>) => void
	clearGrouping: () => void
	setFilter: (
		field: keyof T,
		filter: FilterOperation<T[keyof T], SqlOperator>,
	) => void
	clearFilter: (field: keyof T) => void
	clearAllFilters: () => void
	setSort: (sort: SortState<T> | undefined) => void
	clearSort: () => void
	handleSelectionChanged: (event: SelectionChangedEvent) => void
	handleDateFilterChange: (filter: DateFilterType | undefined) => void
	setDateFilter: (filter: DateFilterType | undefined) => void
	clearDateFilter: () => void
	applyView: (viewId: string) => void
	handleSearchQueryChange: (query: string) => void
	setIsEditingBlocked: (blocked: boolean) => void
	handleViewCreate: (name: string) => Promise<void>
	handleViewUpdate: (id: string, name: string) => Promise<void>
	handleViewDelete: (id: string) => Promise<void>
	handleViewChange: (viewId: string) => void
}

export interface TableProviderConfig<T> {
	columns: ColumnConfig<T>[]
	dataSource: TableDataSource<T>
	features?: TableFeatures<T>
	cellRenderers?: TableCellRenderers<T>
	headerRenderers?: TableHeaderRenderers<T>
	rowModel?: TableRowModel
	disableAutoSize?: boolean
}

export enum TableRowModel {
	ClientSide = 'clientSide',
	ServerSide = 'serverSide',
}

export enum ColumnDataType {
	String = 'string',
	Number = 'number',
	Date = 'date',
	Enum = 'enum',
	Boolean = 'boolean',
}

export const columnDataTypeOperators: Record<ColumnDataType, SqlOperator[]> = {
	[ColumnDataType.String]: [
		SqlOperator.EQUALS,
		SqlOperator.NOT_EQUALS,
		SqlOperator.CONTAINS,
		SqlOperator.NOT_CONTAINS,
		SqlOperator.STARTS_WITH,
		SqlOperator.ENDS_WITH,
		SqlOperator.IS_NULL,
		SqlOperator.IS_NOT_NULL,
	],
	[ColumnDataType.Number]: [
		SqlOperator.EQUALS,
		SqlOperator.NOT_EQUALS,
		SqlOperator.GREATER_THAN,
		SqlOperator.LESS_THAN,
		SqlOperator.GREATER_THAN_OR_EQUALS,
		SqlOperator.IS_NULL,
		SqlOperator.IS_NOT_NULL,
	],
	[ColumnDataType.Date]: [
		SqlOperator.EQUALS,
		SqlOperator.NOT_EQUALS,
		SqlOperator.GREATER_THAN,
		SqlOperator.LESS_THAN,
		SqlOperator.BETWEEN,
	],
	[ColumnDataType.Enum]: [SqlOperator.EQUALS, SqlOperator.NOT_EQUALS],
	[ColumnDataType.Boolean]: [SqlOperator.EQUALS],
}

export type ColumnConfig<T> = Omit<ColDef<T>, 'field'> & {
	field: keyof T
	header: string
	width?: number
	sortable?: boolean
	dataType?: ColumnDataType
	options?: Array<{ label: string; value: any }>
}

export interface BaseTableRow {
	id: string
}

export interface GroupableField<T> {
	field: keyof T
}

// Helper for disabled state (still relevant, but simplified)
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export function isActionDisabled<T>(
	action: BulkActionButton<T> | BulkActionMenuItem<T>,
	selectedItems: T[],
): boolean {
	return typeof action.disabled === 'function'
		? action.disabled(selectedItems)
		: !!action.disabled
}

// Type predicates
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export function isMenuBranch<T>(
	action: BulkActionMenuItem<T>,
): action is BulkActionBranch<T> {
	return 'children' in action && action.children !== undefined
}

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export function isMenuLeaf<T>(
	action: BulkActionMenuItem<T>,
): action is BulkActionLeaf<T> {
	return 'handler' in action && action.handler !== undefined
}

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export function isCategoryBranch<T>(
	category: FilterCategory<T>,
): category is FilterCategoryBranch<T> {
	return 'children' in category && category.children !== undefined
}

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export function isCategoryLeaf<T>(
	category: FilterCategory<T>,
): category is FilterCategoryLeaf<T> {
	return 'field' in category
}

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export function isFilterableDataSource<T>(
	dataSource: TableDataSource<T>,
): dataSource is FilterableTableDataSource<T> {
	return (
		typeof (dataSource as FilterableTableDataSource<T>).refetch === 'function'
	)
}

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export function isServerSideDataSource<T>(
	dataSource: TableDataSource<T>,
): dataSource is ServerSideTableDataSource<T> {
	return (
		typeof (dataSource as ServerSideTableDataSource<T>).fetchBuckets ===
		'function'
	)
}

interface ContextMenuItem<T> {
	name: string
	action: (data: T) => void
}

interface ContextMenuSection<T> {
	items: ContextMenuItem<T>[]
}

interface ContextMenuConfig<T> {
	sections?: ContextMenuSection<T>[]
	includeDefaults?: boolean
}

export interface EditingConfig<T> {
	onValueChange: (field: keyof T, newValue: any, row: T) => T
	excludedFields?: (keyof T)[]
	singleClickEdit?: boolean
	stopEditingWhenCellLosesFocus?: boolean
}

export interface EditableField<T> {
	field: keyof T
}

export const SEARCH_QUERY_SEPARATOR = '@#$%'
